import { LAUNCH_DARKLY_KEYS } from "@granular/fabric3-definitions";
import {
  asyncWithLDProvider,
  useLDClient,
  type LDContext,
} from "launchdarkly-react-client-sdk";
import React from "react";
import { useAtomValue } from "jotai";
import { datadogRum } from "@datadog/browser-rum";
import { getEnvironment } from "../helpers/environment";
import Loading from "../components/Loading";
import {
  getContainerNameFromUrl,
  useCountryFromUrl,
} from "../hooks/useCountryFromUrl";
import { useJwtUser } from "../hooks/useJwtUser";
import { usePreferTargetsFeatureFlags } from "../hooks/useImpersonation";
import { isEmbeddedState } from "../state/mobile";
import { buildLdContext } from "./buildLdContext";

/** The context `LDProvider` will initialize the client with. See `captureContext`. */
let initialContext: LDContext | undefined;

/**
 * Serialized context the client is currently evaluating for — `undefined` while
 * it's still anonymous. Outlives any single mount of the components below, so a
 * remount doesn't re-`identify` a context the client already has.
 */
let identifiedContext: string | undefined;

let contextCaptured = false;

/**
 * Hand the first render's context to `LDProvider` below.
 *
 * This is a module-level latch rather than component state on purpose. `Wrapper`
 * gets mounted more than once — `ThirdParty`, directly above it in the Shell,
 * renders its children both as its `Suspense` fallback and inside the lazy
 * Segment provider, and every flip of that boundary tears our subtree down and
 * builds a new one. Anything per-mount (`useState`, `useRef`) would hand a
 * fresh value to a fresh client on each of those mounts.
 */
const captureContext = (context: LDContext | undefined) => {
  if (contextCaptured) {
    return;
  }

  contextCaptured = true;
  initialContext = context;
  identifiedContext = JSON.stringify(context);
};

/**
 * The one LD client for this page load.
 *
 * As we depend on LD flags to decide whether an application is
 * visible/routable in the Container, the provider has to be loaded before the
 * Container renders — but we don't need to wait on *remote* flags, because
 * `bootstrap: "localStorage"` gives us values synchronously. That's what keeps
 * the app from flickering as flags arrive at startup.
 *
 * NOTE: The `context` matters a great deal here. The SDK keys its
 * `localStorage` bootstrap cache by the context, so initializing *with the
 * identified user* means the values we read on the very first render are that
 * user's values. Initializing anonymously (and identifying later in an effect)
 * used to mean the first render saw the *anonymous* context's flags — fine for
 * anything that re-renders on change, but wrong for one-shot decisions like
 * `<Navigate>` in the Container's Homepage, which unmounts before the real
 * values ever arrive.
 *
 * `React.lazy` memoizes its factory — it runs at most once for a given lazy
 * component, no matter how often that component is mounted — so declaring this
 * at module scope is what guarantees a single client, connection and event
 * queue. Building it inside the component (even in a `useState` initializer)
 * gives every remount its own client instead.
 *
 * The factory reads `initialContext` rather than taking it as an argument
 * because of that memoization: React only invokes it once it starts rendering
 * this element, which is strictly after `Wrapper`'s render has latched the
 * context. `LDContextSync` handles every change from then on.
 */
const LDProvider = React.lazy(async () => ({
  default: await asyncWithLDProvider({
    clientSideID: LAUNCH_DARKLY_KEYS[getEnvironment()],
    // When undefined, the SDK creates an anonymous context for us.
    context: initialContext,
    reactOptions: { useCamelCaseFlagKeys: false },
    // we don't need to wait for remote flags, we initially the app with local
    // storage values
    options: {
      bootstrap: "localStorage",
      inspectors: [
        // Setup the DD Rum FF inspector. This reports to DD what flag values are being used.
        {
          type: "flag-used",
          name: "dd-inspector",
          method: (key, detail) => {
            datadogRum.addFeatureFlagEvaluation(key, detail.value);
          },
        },
      ],
    },
    // the amount of time, in seconds, to wait for initialization before
    // rejecting the promise.
    // CAT-7143: bumping up to 10s due to power users reporting FF not loading for them.
    timeout: 10, // 10 seconds
  }),
}));

/**
 * Keeps the LD client's context in sync *after* initialization.
 *
 * The provider is created once, with the context available at mount. Anything
 * that changes afterwards — the impersonation toggle swapping the target user,
 * `isEmbedded` being set from the query string, a token arriving late — lands
 * here and gets re-`identify`-ed.
 */
const LDContextSync: React.FC<
  React.PropsWithChildren<{ context: LDContext | undefined }>
> = ({ context, children }) => {
  const ldClient = useLDClient();

  React.useEffect(() => {
    if (!ldClient || !context) {
      return;
    }

    const serialized = JSON.stringify(context);

    // No-ops on mount: `captureContext` recorded what the client was
    // initialized with, so we only pay for an `identify` round trip when
    // something has genuinely changed since.
    if (serialized === identifiedContext) {
      return;
    }

    identifiedContext = serialized;
    void ldClient.identify(context);
  }, [ldClient, context]);

  return children;
};

const Wrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  const user = useJwtUser();
  const country = useCountryFromUrl();
  const containerName = getContainerNameFromUrl();
  const [preferTargetsFeatureFlags] = usePreferTargetsFeatureFlags();
  const isEmbedded = useAtomValue(isEmbeddedState);
  const hostname = globalThis.location.hostname;

  const context = React.useMemo(
    () =>
      buildLdContext({
        user,
        preferTargetsFeatureFlags,
        country,
        containerName,
        isEmbedded,
        hostname,
      }),
    [
      user,
      preferTargetsFeatureFlags,
      country,
      containerName,
      isEmbedded,
      hostname,
    ],
  );

  /**
   * `AuthGuard` sits above us in the Shell and `useJwtUser` is synchronous, so
   * the context from our first render is the identified user's in every normal
   * case — which is the whole point of initializing the client with it.
   */
  captureContext(context);

  return (
    <React.Suspense fallback={<Loading />}>
      <LDProvider>
        <LDContextSync context={context}>{children}</LDContextSync>
      </LDProvider>
    </React.Suspense>
  );
};

export default Wrapper;
